const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      throw new Error('Login failed');
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  getProfile: () => makeAuthenticatedRequest('/auth/profile'),
  
  changePassword: (currentPassword, newPassword) => 
    makeAuthenticatedRequest('/users/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
};

// Players API
export const playersAPI = {
  getAllPlayers: () => makeAuthenticatedRequest('/users/players'),
  getPlayerBalance: (playerId) => makeAuthenticatedRequest(`/players/${playerId}/balance`),
  grantCoins: (playerId, amount) => 
    makeAuthenticatedRequest(`/players/${playerId}/grant-coins`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    }),
  removeCoins: (playerId, amount) => 
    makeAuthenticatedRequest(`/players/${playerId}/remove-coins`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    }),
  resetPlayerPassword: (playerId, newPassword) => 
    makeAuthenticatedRequest(`/users/player/${playerId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    }),
};

// Requests API (for top-up and cash-out requests)
export const requestsAPI = {
  getPendingRequests: () => makeAuthenticatedRequest('/requests/pending-player-requests'),
  getAllRequests: () => makeAuthenticatedRequest('/requests/transaction-history'),
  approveRequest: (requestId, requestType) =>
    makeAuthenticatedRequest(`/requests/${requestId}/admin-approve`, {
      method: 'POST',
      body: JSON.stringify({ requestType }),
    }),
  rejectRequest: (requestId, reason) => 
    makeAuthenticatedRequest(`/requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  getRequestStats: () => makeAuthenticatedRequest('/requests/stats'),
  createTopUpRequest: (amount, notes) => 
    makeAuthenticatedRequest('/requests/admin-top-up', {
      method: 'POST',
      body: JSON.stringify({ amount, notes }),
    }),
  createCashOutRequest: (amount, notes) => 
    makeAuthenticatedRequest('/requests/cash-out', {
      method: 'POST',
      body: JSON.stringify({ amount, notes }),
    }),
  getMyRequests: () => makeAuthenticatedRequest('/requests/my-requests'),
};

// Audit Logs API
export const auditAPI = {
  getAllLogs: () => makeAuthenticatedRequest('/audit-logs'),
  getUserLogs: (userId) => makeAuthenticatedRequest(`/audit-logs/user/${userId}`),
};

// Chat API
export const chatAPI = {
  getAllMessages: () => makeAuthenticatedRequest('/chat/all-messages'),
  moderateMessage: (messageId, action) => 
    makeAuthenticatedRequest(`/chat/moderate/${messageId}`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),
  getChatStats: () => makeAuthenticatedRequest('/chat/stats'),
};

// House API (for admin management)
export const houseAPI = {
  getAdmins: () => makeAuthenticatedRequest('/house/admins'),
  createAdmin: (adminData) => 
    makeAuthenticatedRequest('/house/create-admin', {
      method: 'POST',
      body: JSON.stringify(adminData),
    }),
  allocateCoinsToAdmin: (adminId, amount) => 
    makeAuthenticatedRequest(`/house/admin/${adminId}/allocate-coins`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    }),
  removeCoinsFromAdmin: (adminId, amount) => 
    makeAuthenticatedRequest(`/house/admin/${adminId}/remove-coins`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    }),
  removeAdmin: (adminId) => 
    makeAuthenticatedRequest(`/house/admin/${adminId}`, {
      method: 'DELETE',
    }),
  getStats: () => makeAuthenticatedRequest('/house/stats'),
};

const apiExports = {
  authAPI,
  playersAPI,
  requestsAPI,
  auditAPI,
  chatAPI,
  houseAPI,
};

export const logout = () => {
  authAPI.logout();
  window.location.reload();
};

export default apiExports;
